/* eslint-disable quote-props */
import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { WindowModalComponent } from './window-modal.component';
import { Util } from '../utils/utils.module';
import { AppComponent } from '../app.component';
import { TabItem, TabReceiver } from '../models/tab-item';
import { ListItem } from '../models/list-item';
import { CommandHandler } from '../models/command-handler';
import { SelectItem } from '../models/form-field';
import { LocalizeService } from '../services/localize.service';
import { FormWrapperComponent } from '../forms/form-wrapper.component';
import { TabSelectorComponent } from '../widgets/tab-selector.component';
import { SelectComponent } from '../widgets/select.component';
import { FilelistReceiver } from '../widgets/filelist-sidebar.component';
import { ActionBarComponent } from '../widgets/action-bar.component';
import { ListTableParent, ListTableComponent } from '../lists/list-table.component';
import { KeyCommand, ShortcutsService } from '../services/shortcuts.service';
import { MenuId } from '../services/menu.service';
import { FilterSidebarComponent } from '../forms/filter-sidebar.component';
import { ListData } from '../models/base';
import { AdminService, AdminTabs } from '../services/admin.service';

@Component({
  selector: 'edx-window-modal-admin',
  styleUrls: ['window-modal.component.scss', 'window-modal-metadata.component.scss', 'window-modal-admin.component.scss'],
  templateUrl: 'window-modal-admin.component.html',
  standalone: false
})
export class WindowModalAdminComponent extends WindowModalComponent implements OnInit, OnDestroy, TabReceiver, CommandHandler, FilelistReceiver, ListTableParent {
  @ViewChild('formWrapper') formWrapper: FormWrapperComponent;
  @ViewChild('tabSelector') tabSelector: TabSelectorComponent;
  @ViewChild('lookupTable') lookupTable: ListTableComponent;
  @ViewChild('actionbar') actionBar: ActionBarComponent;
  @ViewChild(FilterSidebarComponent) filters: FilterSidebarComponent;
  public tabId = '';
  public childTabId = '';
  public showSpinner = true;
  private appComponent: AppComponent = Util.RestAPI.getAppComponent();
  private primaryLib: string = Util.RestAPI.getPrimaryLibrary();
  private urlLoaded = false;
  private showSave = false;
  private leftNavSelectedIndex = 0;
  private leftNavSelectedGroupId = null;
  private groupsData: any[];
  private leftNavList: ListItem[] = [];
  private leftNavListLookupKey = '';
  private lookupTableSelections: ListItem[] = [];
  private listItems: ListItem[] = [];
  private librarySelectItems: SelectItem[];
  private lookupSelItems: SelectItem[];
  private currentLookup: string;
  private lookupDesc: any;
  private lookupForm: string;
  private lookupFormType: any;
  private lookupKey: any;
  private lookupLeadingColums = [0];
  private actionId = MenuId.MENU_LOOKUP_ACTIONS;
  private shortcutsSubscription: Subscription;
  private showFilter = false;
  private filter = '';
  private tabList: TabItem[];
  private childTabList: TabItem[];
  private contentColumns: string[] = [];
  private contentFormKind: string = '';
  private contentAPIKey: string = '';
  private contentLoaded = false;
  private contentSaveCommand: string = '';
  private lastAccessedSection: string = 'details';
  private formData = {};
  public searchValue = '';
  public showInfoMessage = false;
  public infoMessage = '';
  public isFiltering = false;
  public librariesList: any[];
  public labels: any = {};
  private languageLookupForms: any;

  constructor(protected location: Location, protected router: Router, protected route: ActivatedRoute,
    private localizer: LocalizeService, shortcutsService: ShortcutsService,
    private adminService: AdminService) {
    super(location, router, route);
    this.labels = {
      saveStr: this.localizer.getTranslation('FOLDER_ACTIONS.SAVE'),
      resetStr: this.localizer.getTranslation('FORMS.LOCAL.PREFERENCES.RESET'),
      searchByGroup: localizer.getTranslation('FORMS.LOOKUPS.SEARCH_BY_WATERMARK', [localizer.getTranslation('SEARCH_SELECTOR.GROUP')]),
      clear: localizer.getTranslation('REFINE_SEARCH.CLEAR'),
      refineSearch: localizer.getTranslation('REFINE_SEARCH.SEARCH'),
      createGroup: localizer.getTranslation('ADMIN.GROUPS.CREATE_GROUP'),
      noItemsMsg: localizer.getTranslation('PLACEHOLDER.NO_ITEMS')
    };
    this.shortcutsSubscription = shortcutsService.commands.subscribe(c => this.handleCommand(c));
  }

  handleCommand(command: KeyCommand) {
    this.tabSelector.selectTabById(command.name.replace('-', '').toUpperCase());
    setTimeout(() => {
      Util.Transforms.focusOnElementByClassName('fileitem selected');
    }, 100);
  }

  ngOnInit() {
    this.showSpinner = false;
    this.lookupSelItems = [];
    this.initLibrarySection();
    this.initTabsContent();
    this.tabId = this.getFirstEnabledTab();
    if (this.urlLoaded) {
      this.libraryChanged(null);
    }
    if (!Util.RestAPI.isGuestUser()) {
      super.ngOnInit();
    } else {
      Util.Notify.error(this.localizer.getTranslation('RAPI_ERRORS.13'));
      this.appComponent.navBack();
    }
  }

  private initLibrarySection(): void {
    this.librariesList = Util.RestAPI.getLibraries();
    this.librarySelectItems = [];
    if (!!this.librariesList && !!this.librariesList.length) {
      for (const lib of this.librariesList) {
        if (lib.DISABLED === 'N') {
          const libNameUC: string = lib.LIBRARY_NAME.toUpperCase();
          this.librarySelectItems.push({ value: libNameUC, display: lib.LIBRARY_NAME, disabled: !this.adminService.canUserAccessAnyAdminTab(lib) });
        }
      }
    }
  }

  ngOnDestroy() {
    if (this.shortcutsSubscription) {
      this.shortcutsSubscription.unsubscribe();
    }
    this.resetFormData();
    this.resetData();
  }

  private initTabsContent(): void {
    this.tabList = [
      { id: AdminTabs.GENERAL, title: 'FORMS.LOCAL.PREFERENCES.GENERAL', selected: false },
      { id: AdminTabs.LOOKUPS, title: 'ADMIN.VALIDATION_TABLES', selected: false },
      { id: AdminTabs.TILES, title: 'FORMS.LOCAL.PREFERENCES.HOME_TILES', selected: false },
      { id: AdminTabs.FOOTER, title: 'ADMIN.FOOTER.FOOTER', selected: false },
      { id: AdminTabs.PEOPLE, title: 'ADMIN.PEOPLE', selected: false },
      { id: AdminTabs.GROUPS, title: 'SECURITY.USER_GROUP.GROUPS', selected: false },
      { id: AdminTabs.SYSTEM_PARAMS, title: 'ADMIN.SYSTEM_PARAMS', selected: false },
    ];
  }

  private getFirstEnabledTab(): string {
    for (const tab of this.tabList) {
      if (this.tabEnabled(tab.id)) {
        return tab.id;
      }
    }
    return '';
  }

  private initLookupForms(): void {
    const keys = [
      'PEOPLE',
      '_GROUPS',
      '_PEOPLE_ALL',
      '_GROUPS_ALL',
      '_GROUPS_ENABLED',
      'TERMINOLOGY'
    ];
    this.languageLookupForms = keys.reduce((forms, key) => {
      forms[key] = Util.RestAPI.getLookupTableName(key, this.desc.lib);
      return forms;
    }, {});
  }

  private getLanguageForm(form: string): string {
    return this.languageLookupForms?.[form] || form;
  }

  private okEnabled(event: any): void {
    this.enableSave(event.dirty && event.enable);
  }

  private enableSave(enable: boolean): void {
    if (this.showSave !== enable) {
      this.showSave = enable;
      this.appComponent.blockForDirty(this, enable);
    }
  }

  public libraryChangeEvent(event): void {
    this.desc['lib'] = event?.value;
    this.initTabsContent();
    if (!this.tabEnabled(this.tabId)) {
      this.tabId = this.getFirstEnabledTab();
    }
    this.tabList.forEach(tab => {
      tab.selected = (tab.id === this.tabId);
    });
    this.resetData();
    this.libraryChanged(event);
  }

  private libraryChanged(selectComponent: SelectComponent): void {
    let queryArgs = '';
    let params = '';
    if (!!selectComponent) {
      this.desc.lib = selectComponent.value;
    }
    if (!!this.desc && this.desc.lib) {
      queryArgs = '?library=' + this.desc.lib;
    }
    this.initLookupForms();
    if (this.tabId === AdminTabs.LOOKUPS) {
      params = 'modifiabletables';
      this.initContentSection();
      this.loadData(params, queryArgs);
    } else {
      this.searchValue = '';
      this.initContentSection();
      if (this.tabId === AdminTabs.SYSTEM_PARAMS) {
        this.loadRightPane();
      } else if (this.desc?.lib || this.primaryLib) {
        this.searchLeftPane();
      }
    }
  }

  private loadData(params, queryArgs): void {
    Util.RestAPI.get('lookups', params, queryArgs).toPromise().then(data => {
      if (!!data) {
        this.contentLoaded = true;
        if (this.tabId === AdminTabs.LOOKUPS) {
          this.leftNavList = data.map((table) => new ListItem({ id: table.formName, type: 'lookups', lib: this.desc?.lib, DOCNAME: table.formTitle, DOCNUM: table.formId, secured: table.lookupsecurity === 'Y' }));
          this.lookupSelItems = this.leftNavList.map(aii => ({ value: aii.id, display: aii.DOCNAME }) as SelectItem);
        } else if (this.tabId === AdminTabs.PEOPLE) {
          this.leftNavList = [];
        } else {
          const listData = data.list.map(x => x.item);
          this.buildGroupsData(listData);
          const groupData = listData.map(x => x.filter(y => y.PROPNAME === 'GROUP_ID'));
          this.leftNavList = groupData?.map((group) => new ListItem({ id: group[0].PROPNAME, type: 'lookups', lib: this.desc?.lib, DOCNAME: group[0].DATA, DOCNUM: group[0].TITLE, APP_ID: params }));
          this.showInfoMessage = data.set.total === data.set.max;
          this.infoMessage = this.localizer.getTranslation('FORMS.LOOKUPS.INFO_MORE', [data.set.max.toString()]);
          this.isFiltering = false;
        }
        if (this.tabId === AdminTabs.GROUPS) {
          if (this.lastCmd === 'save_groups' && !!this.leftNavSelectedGroupId) {
            this.leftNavSelectedIndex = this.leftNavList?.findIndex(item => item.DOCNAME === this.leftNavSelectedGroupId);
            if (this.leftNavSelectedIndex < 0) {
              this.leftNavSelectedIndex = 0;
            }
          }
          this.contentLoaded = data.list.length > 0;
        }
        this.loadRightPane();
      } else {
        this.leftNavList = [];
      }
    }, err => {
      this.leftNavList = [];
    });
  }

  private initContentSection(): void {
    this.contentColumns = [];
    this.contentLoaded = false;
    this.contentSaveCommand = this.tabId === AdminTabs.LOOKUPS ? '' : 'save_' + this.tabId.toLowerCase();
    switch (this.tabId) {
      case AdminTabs.GENERAL:
        this.contentColumns.push(this.localizer.getTranslation('ADMIN.TILES.USER_OVERRIDE'));
        this.contentFormKind = '__local_preferences_general';
        this.contentAPIKey = 'GroupGeneralSettings';
        break;
      case AdminTabs.TILES:
        this.contentColumns.push(this.localizer.getTranslation('ADMIN.TILES.AVAILABLE'));
        this.contentColumns.push(this.localizer.getTranslation('ADMIN.TILES.USER_OVERRIDE'));
        this.contentFormKind = '__local_group_tile_settings';
        this.contentAPIKey = 'GroupTileSettings';
        break;
      case AdminTabs.FOOTER:
        this.contentColumns.push(this.localizer.getTranslation('ADMIN.FOOTER.ENABLE'));
        this.contentFormKind = '__local_group_footer_defaults';
        this.contentAPIKey = 'GroupFooterDefaults';
        break;
      case AdminTabs.GROUPS:
        this.contentFormKind = '__local_group_edit';
        this.contentAPIKey = 'permissions';
        break;
      case AdminTabs.SYSTEM_PARAMS:
        this.contentFormKind = '__local_system_params';
        this.contentAPIKey = 'defaults';
        break;
    }
  }

  private loadRightPane(): void {
    switch (this.tabId) {
      case AdminTabs.LOOKUPS:
      case AdminTabs.PEOPLE:
        this.loadLookupsList();
        break;
      case AdminTabs.GENERAL:
      case AdminTabs.TILES:
      case AdminTabs.FOOTER:
        this.loadGroupSettings();
        break;
      case AdminTabs.GROUPS:
        this.loadGroupLevelConfigurations();
        break;
      case AdminTabs.SYSTEM_PARAMS:
        this.loadSystemDefaults();
        break;
    }
  }

  private loadLookupsList(): void {
    if (this.tabId === AdminTabs.PEOPLE) {
      this.currentLookup = this.getLanguageForm(this.tabId);
      this.lookupDesc = new ListItem({ DOCNAME: this.localizer.getTranslation('SEARCH_SELECTOR.USER'), DOCNUM: '', id: this.getLanguageForm('PEOPLE'), lib: this.desc.lib, type: 'lookups' });
      this.lookupDesc['listLookupId'] = this.getLanguageForm('_PEOPLE_ALL');
      this.lookupForm = this.getLanguageForm(this.tabId);
      this.lookupKey = 'USER_ID';
    } else {
      this.currentLookup = this.lookupSelItems.length > 0 ? this.lookupSelItems[this.leftNavSelectedIndex].value : undefined;
      this.lookupDesc = this.leftNavList[this.leftNavSelectedIndex];
      if (this.lookupDesc) {
        this.lookupDesc['listLookupId'] = this.currentLookup;
      }
      this.lookupForm = this.leftNavList[this.leftNavSelectedIndex]?.id;
      this.lookupKey = this.currentLookup + '_ID';
    }
    this.lookupFormType = 'profile';
    const lib = this.desc.lib;
    this.desc = { id: '', type: '', lib: lib };
    Util.Transforms.scrollIntoViewIfNeededUsingClassName('selected fileitem');
    setTimeout(() => {
      this.setDesc(lib);
      if (!!this.lookupTable) {
        this.lookupTableSelections = [];
        this.handleFilterChange();
        this.contentLoaded = true;
        this.selectionsUpdated(this.lookupTable);
      }
    }, 10);
  }
  private loadGroupSettings(): void {
    Util.RestAPI.getGroupSettings(this.getSelectedGroupId(), this.desc.lib, this.contentAPIKey).then((data) => {
      this.setFormDataForSelectedTab(this.getSelectedGroupId(), data);
    });
  }

  private loadGroupLevelConfigurations(): void {
    setTimeout(() => {
      this.loadGroupPermissions(this.getSelectedGroupId());
    }, 1);
  }

  private loadGroupPermissions(groupId: string): void {
    const groupSystemId = this.getSelectedGroupSystemId(groupId);
    if (groupSystemId) {
      this.adminService.getGroupLevelPermissions(groupSystemId, this.desc?.lib).subscribe((data) => {
        this.setFormDataForSelectedTab(groupId, data);
      });
    }
  }

  private loadSystemDefaults(): void {
    this.adminService.getSystemParams(Util.RestAPI.getLibraryByName(this.desc?.lib)).subscribe((data) => {
      const queryArgs = `?library=${this.desc?.lib || Util.RestAPI.getPrimaryLibrary()}`;
      Util.RestAPI.getFooterOptions('LIBRARY', 'footeroptions', queryArgs).then((res) => {
        data['footer_xml'] = res['userxml'];
        data['footer_xsd'] = res['userxsd'];
        this.setFormDataForSelectedTab(null, data);
      });
    });
  }

  private getSelectedGroupId(): string {
    return this.leftNavList?.[this.leftNavSelectedIndex]?.DOCNAME;
  }

  private getSelectedGroupSystemId(groupId: string): string {
    const group = this.groupsData.find(group => group['GROUP_ID'] === groupId);
    if (!!group) {
      return group['%PRIMARY_KEY'];
    }
    return null;
  }

  private setFormDataForSelectedTab(groupId: string, data: any) {
    let formData = {};
    if (this.tabId !== AdminTabs.SYSTEM_PARAMS) {
      const groupSystemId = this.getSelectedGroupSystemId(groupId);
      formData = { GROUPID: groupId, GROUP_SYSTEM_ID: groupSystemId, SELECTED_LIB: this.desc?.lib };
      if (this.tabId === AdminTabs.GROUPS) {
        if (!!data['%GROUP_MEMBERS']) {
          data['AUTHOR_ID'] = data['%GROUP_MEMBERS'];
          delete data['%GROUP_MEMBERS'];
        }
      }
    }
    formData[this.tabId] = data;
    this.updateFormData(formData);
    this.contentLoaded = true;
    this.showSpinner = false;
    Util.Transforms.scrollIntoViewIfNeededUsingClassName('selected fileitem');
  }

  private updateFormData(formData): void {
    this.formData = formData;
  }

  private resetFormData(): void {
    this.formWrapper = null;
    this.formData = null;
  }

  private handleFilterChange(filter = '') {
    if (!!this.lookupTable) {
      this.filter = filter;
      this.lookupTable.handleFilterChange(filter, this.lookupKey, 'includeDisabled');
      if (!!this.filters) {
        this.filters.enableOK(false, true);
        setTimeout(() => {
          if (!filter && !!this.filters) {
            this.filters.getAndSetFormData();
          }
        }, 500);
      }
      this.lookupTableSelections = [];
      if (!!this.actionBar) {
        this.actionBar.updateActions();
      }
    }
  }

  private setDesc(lib: string): void {
    this.desc = ({ id: 'admin', type: 'admin', lib, imgPath: 'assets/images/console_settings_grey.svg' } as any);
  }

  public getDesc(): any {
    return this.desc;
  }

  protected loadURL(url: string): void {
    // block the base class from loading the url
    this.appComponent.registerWindow(this, url);
    this.setDesc(Util.RestAPI.getPrimaryLibrary().toUpperCase());
    if (!this.showSpinner) {
      this.libraryChanged(null);
    }
    this.urlLoaded = true;
  }

  public canShowChildTabs(tabId): boolean {
    return tabId === '';
  }

  public initialList(table: ListTableComponent): ListItem[] {
    return this.listItems;
  }

  public initialSet(table: ListTableComponent): any {
    return { total: this.listItems.length };
  }

  public tabSelected(id: string): void {
    this.tabId = id;
    this.resetData();
    this.libraryChanged(null);
  }

  public tabEnabled(id: string): boolean {
    if (Util.Device.isWebLook()) {
      const lib = Util.RestAPI.getLibraryByName(this.desc.lib);
      switch (id) {
        case AdminTabs.GENERAL:
        case AdminTabs.TILES:
        case AdminTabs.FOOTER:
          return this.adminService.canUserManageAdminFeature(id);
        case AdminTabs.LOOKUPS:
          return this.adminService.canUserManageLookups(lib);
        case AdminTabs.GROUPS:
          return this.adminService.canUserManageUsersAndGroups(lib) || this.adminService.canUserManageSystemParams(lib);
        case AdminTabs.SYSTEM_PARAMS:
          return this.adminService.canUserManageSystemParams(lib);
        case AdminTabs.PEOPLE:
          return this.adminService.canUserManagePeople(lib);
      }
    }
    return false;
  }

  public fileSelected(index: number): void {
    // load a file from the list into the content panel
    if ((index >= 0 && index < this.leftNavList.length) || this.tabId === AdminTabs.SYSTEM_PARAMS) {
      this.leftNavSelectedIndex = index;
      this.leftNavSelectedGroupId = null;
      this.loadRightPane();
    }
  }

  public cancelHdrBtnClick(): void {
    if (this.formWrapper?.dynamicForm.footerConfigurationFullScreen) {
      return;
    }
    if (this.showSave) {
      this.showSpinner = true;
      setTimeout(() => {
        this.enableSave(false);
        this.showSpinner = false;
      }, Util.kPopupDismissMS);
    }
  }

  public rightHdrBtnClick(event?: KeyboardEvent): void {
    const target = event?.target as HTMLElement;
    if ((event?.key === 'Enter' || event?.key === 'Space') && target?.tagName.toLowerCase() === 'textarea') {
      return;
    }
    if (!!this.contentSaveCommand && this.showSave) {
      this.doCommand(this.contentSaveCommand);
    }
  }

  public createGroup(event: KeyboardEvent): void {
    Util.Transforms.preventDefaultAndStopPropagation(event);
    this.doCommand('group_create');
  }

  public doCommand(cmd: string): boolean {
    this.lastCmd = cmd;
    switch (cmd) {
      case 'lookup_copy':
      case 'lookup_edit':
      case 'lookup_add':
      case 'lookup_filter':
      case 'lookup_security':
        this.doCommandForLookup(cmd);
        break;
      case 'group_create':
        this.appComponent.doCommand(cmd);
        break;
      case 'save_tiles':
      case 'save_footer':
      case 'save_general':
      case 'save_system_params':
        this.doCommandForGroup(cmd);
        break;
      case 'save_groups':
        if (!this.formWrapper || !Util.FieldMappings.isProfilePreviewForm(this.formWrapper?.kind)) {
          this.lastAccessedSection = this.formWrapper.dynamicForm?.getLastAccessedSection() || '';
          this.doCommandForGroup(cmd);
        }
        break;
    }
    return false;
  }

  public doCommandForGroup(cmd: string): boolean {
    switch (cmd) {
      case 'save_tiles':
      case 'save_footer':
      case 'save_general':
      case 'save_groups':
      case 'save_system_params':
        this.showSpinner = true;
        if (this.formWrapper) {
          this.formWrapper.popupOK();
        }
        setTimeout(() => {
          this.enableSave(false);
          this.showSpinner = false;
        }, Util.kPopupDismissMS);
        if (this.tabId === AdminTabs.GROUPS || this.tabId === AdminTabs.SYSTEM_PARAMS) {
          this.showSpinner = true;
          this.contentLoaded = false;
          if (cmd === 'save_groups' && !!this.formData?.['GROUP_ID']) {
            this.leftNavSelectedGroupId = this.formData?.['GROUP_ID'];
          }
        } else {
          setTimeout(() => {
            this.fileSelected(this.leftNavSelectedIndex);
          }, 1);
        }
        break;
    }
    return false;
  }

  public doCommandForLookup(cmd: string): boolean {
    const selectedLookupItem = Util.RestAPI.deepCopy(this.lookupDesc);
    selectedLookupItem['FORM_NAME'] = this.getLanguageForm(selectedLookupItem.id);
    switch (cmd) {
      case 'lookup_copy':
      case 'lookup_edit':
        selectedLookupItem['DOCNUM'] = this.lookupTableSelections[0]['%PRIMARY_KEY'];
        this.appComponent.doCommand('profile_' + cmd, { desc: selectedLookupItem });
        break;
      case 'lookup_add':
        this.appComponent.doCommand('profile_' + cmd, { desc: selectedLookupItem });
        break;
      case 'lookup_filter':
        this.showFilter = !this.showFilter;
        if (!this.showFilter && this.filters?.isFiltered) {
          this.handleFilterChange();
        }
        setTimeout(() => {
          this.initAndOpenFilters();
          if (this.tabId === AdminTabs.PEOPLE) {
            this.lookupTable.resetVisibleColumnCount();
          }
        }, 10);
        break;
    }
    return false;
  }

  public toggle(toggleOn: boolean, toggleDesc: any): void {
    if (toggleDesc.cmd === 'lookup_security') {
      const formData = { 'lookupsecurity': toggleOn ? 'Y' : 'N' };
      const toggleDesc2 = { type: 'lookups', id: Util.RestAPI.getLookupTable(this.lookupDesc.id, this.lookupDesc.lib, null), lib: this.lookupDesc.lib };
      Util.RestAPI.post(toggleDesc2, formData, 'lookupsecurity').subscribe(() => {
        this.lookupDesc['secured'] = toggleOn;
      }, error => {
        Util.Notify.error(this.localizer.getTranslation('ADMIN.LOOKUP_SECURITY.LOOKUP_SECURITY_ERROR'));
        setTimeout(() => {
          this.actionBar.updateActions();
        }, 200);
      });
    }
  }

  public getToggleState(): boolean {
    return this.lookupDesc?.secured;
  }

  public selectionsUpdated(table: ListTableComponent): void {
    if (!!this.lookupTable) {
      this.lookupTableSelections = this.lookupTable.getSelections();
    }
    if (!!this.actionBar) {
      this.actionBar.updateActions();
    }
  }

  public refresh(cmd?: string, value?: any): void {
    if (this.lastCmd === 'group_create' || this.lastCmd === 'save_groups') {
      const shouldSelectGroup = (cmd && value && this.leftNavSelectedGroupId !== value) || (this.lastCmd === 'save_groups' && !Util.safeHasOwn(this.formData, 'GROUP_ID'));
      if (shouldSelectGroup) {
        this.fileSelected(this.leftNavSelectedIndex);
      } else {
        this.searchLeftPane();
      }
      if (!!this.lastAccessedSection) {
        const focusOnLastAccessedSection = () => {
          if (this.contentLoaded) {
            Util.Transforms.scrollIntoViewByQuerySelector(`#edx-detail-field.${this.lastAccessedSection} summary`);
          } else {
            setTimeout(() => {
              focusOnLastAccessedSection();
            }, 100);
          }
        };
        focusOnLastAccessedSection();
      }
    } else {
      if (this.tabId === AdminTabs.LOOKUPS || this.tabId === AdminTabs.PEOPLE) {
        this.handleFilterChange(this.showFilter ? this.filter : '');
      } else {
        this.fileSelected(this.leftNavSelectedIndex);
      }
    }
  }

  public commandEnabled(cmd: string): boolean {
    const excludeTables = [this.getLanguageForm('TERMINOLOGY'), this.getLanguageForm('PEOPLE')];
    switch (cmd) {
      case 'lookup_add':
      case 'lookup_filter':
        return true;
      case 'lookup_separator':
      case 'lookup_copy':
      case 'lookup_edit':
        return !!this.lookupTableSelections && this.lookupTableSelections.length === 1;
      case 'lookup_security':
        return Util.RestAPI.getLibraryEffectiveRights(this.desc.lib)?.['LOOKUP_SECURITY'] === 'Y' && Util.RestAPI.isAdminFeatureEnabled('LOOKUP_SECURITY') && excludeTables.indexOf(this.lookupForm) === -1;
      case 'group_create':
        return this.canCreateNewGroup();
    }
    return false;
  }

  public getListData(): ListData {
    const list = !!this.lookupTable && !!this.lookupTable.getList() ? this.lookupTable.getList() : [];
    const set = !!this.initialSet ? this.initialSet : {};
    if (!set['search']) {
      set['search'] = {};
    }
    set['search']['FORM_NAME'] = this.currentLookup;
    return { set, list };
  }

  public searchClicked(event: Event, clear?: boolean): void {
    if (!this.showSave) {
      if (event.type === 'keyup') {
        event.preventDefault();
        event.stopPropagation();
      }
      this.isFiltering = true;
      if (!this.searchValue) {
        clear = true;
      }
      if (clear) {
        this.searchValue = '';
        document.getElementById('search')?.focus();
      } else {
        if (event.type === 'keyup' && event.target['id'] && event.target['id'] === 'edx_search_group_btn') {
          setTimeout(() => {
            document.getElementById(event.target['id'])?.focus();
          }, 300);
        }
      }
      this.leftNavSelectedIndex = 0;
      this.searchLeftPane();
    }
  }

  private searchLeftPane(): void {
    let groupsParam = '_GROUPS_ENABLED';
    let allGroups = '';
    const searchFilter = !!this.searchValue ? `&filter=GROUP_ID=*${this.searchValue}* or GROUP_NAME=*${this.searchValue}*` : '';
    if (this.tabId === AdminTabs.GROUPS) {
      groupsParam = '_GROUPS_ALL';
      allGroups = '&includeDisabled';
    }
    this.leftNavListLookupKey = this.getLanguageForm(groupsParam);
    const library = this.desc.lib || this.primaryLib;
    const queryArgs = `?library=${library}&profile=v_groups&key=GROUP_ID${allGroups}${searchFilter}`;
    this.loadData(this.leftNavListLookupKey, queryArgs);
  }

  private buildGroupsData(data): void {
    this.groupsData = [];
    for (const itemStructure of data) {
      const group = {};
      for (const item of itemStructure) {
        group[item['PROPNAME']] = item['DATA'];
      }
      this.groupsData.push(group);
    }
  }

  public resetData(): void {
    this.showFilter = false;
    this.searchValue = '';
    this.showInfoMessage = false;
    this.leftNavSelectedIndex = 0;
    this.leftNavSelectedGroupId = null;
    this.lastCmd = '';
    if (!!this.filters) {
      this.filters.close();
    }
    this.leftNavList = [];
  }

  public clearSearchWhenEmpty(event: KeyboardEvent): void {
    if (!this.searchValue && event.key === 'Backspace') {
      this.searchClicked(event, true);
    } else {
      Util.Transforms.preventDefaultAndStopPropagation(event);
      return;
    }
  }

  public focusOnSpecificElement(): void {
    switch (this.tabId) {
      case AdminTabs.GENERAL:
      case AdminTabs.TILES:
      case AdminTabs.GROUPS:
        Util.Transforms.focusOnElementByQuerySelector('#edx-detail-field summary');
        break;
      case AdminTabs.FOOTER:
        Util.Transforms.focusOnElementById('edx_show_footer_options');
        break;
    }
  }

  public canShowLeftPane(tabId) {
    return tabId === AdminTabs.SYSTEM_PARAMS || (tabId === AdminTabs.PEOPLE && !this.showFilter) ? false : true;
  }

  public areHeaderButtonsDisabled(): boolean {
    return !this.showSave;
  }

  public isAdminLookupCommand(): boolean {
    return ['lookup_copy', 'lookup_edit', 'lookup_add'].includes(this.lastCmd);
  }

  public onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowUp':
        if (event.ctrlKey) {
          Util.Transforms.focusOnFirstActionBarItem();
          Util.Transforms.preventDefaultAndStopPropagation(event);
        }
    }
  }

  public initAndOpenFilters(): void {
    if (!!this.filters) {
      this.filters.ngOnInit();
      this.filters.isOpen = this.showFilter;
    }
  }

  public canCreateNewGroup(): boolean {
    return this.adminService.canUserManageUsersAndGroups() && this.tabId === AdminTabs.GROUPS;
  }

  public showSearchBar(): boolean {
    return this.tabId !== AdminTabs.LOOKUPS && this.tabId !== AdminTabs.SYSTEM_PARAMS && this.tabId !== AdminTabs.PEOPLE;
  }

  public listUpdated(table: ListTableComponent): void {
    table?.clearSelection();
  }
}
